import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
  type RefObject,
} from 'react';
import { useDebounceValue } from 'usehooks-ts';
import type { GridApi, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { isEqual } from 'radash';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { HttpRequest, HighlightColor } from '../store/proxy-store';
import type {
  RequestMetadataRow,
  HistoryFilters,
  ApplicationScopeFilter,
} from '../electron/search-db';
import {
  HttpHeadersRecordSchema,
  parseStoredJsonField,
  ProxyModifiedSnapshotSchema,
  StringArraySchema,
} from '../utils/history-metadata-schemas';
import { useProxyStore } from '../store/proxy-store';
import { useProjectStore } from '../store/projectStore';
import { TARGET_TYPE } from '../utils/target-scope.utils';
import {
  useTabScopedRequestsFilters,
  type ExtensionFilterMode,
  type FilterMode,
  type MethodFilter,
  type MimeTypeFilter,
  type SortField,
  type SortOrder,
  type SourceFilter,
  type StatusFilter,
} from '../store/tab/requestsTabScopedFilterStore';
import { useFilterPresetsStore } from '../store/filterPresetsStore';
import { useExclusionRulesStore } from '../store/exclusionRulesStore';
import {
  translateTqlToRequestSql,
  type RequestSqlTranslation,
} from '../utils/tql/tql-to-sql';
import {
  captureAgGridRowScrollAnchor,
  detectAgGridScrollEdge,
  getAgGridViewport,
  restoreAgGridRowScrollAnchor,
} from './agGridScrollAnchor';

const CACHE_BLOCK_SIZE = 100;
const MAX_BLOCKS_IN_CACHE = 20;
const HISTORY_FILTER_DEBOUNCE_MS = 400;

function parseStatusFilter(
  statusFilter: string,
): { statusMin: number; statusMax: number } | null {
  switch (statusFilter) {
    case '2xx':
      return { statusMin: 200, statusMax: 300 };
    case '3xx':
      return { statusMin: 300, statusMax: 400 };
    case '4xx':
      return { statusMin: 400, statusMax: 500 };
    case '5xx':
      return { statusMin: 500, statusMax: 600 };
    default:
      return null;
  }
}

function rowToHttpRequest(row: RequestMetadataRow): HttpRequest {
  const headers =
    parseStoredJsonField(HttpHeadersRecordSchema, row.request_headers) ?? {};
  const rawHeaders = parseStoredJsonField(
    StringArraySchema,
    row.request_raw_headers,
  );
  const responseHeaders = parseStoredJsonField(
    HttpHeadersRecordSchema,
    row.response_headers,
  );
  const responseRawHeaders = parseStoredJsonField(
    StringArraySchema,
    row.response_raw_headers,
  );
  const tags = parseStoredJsonField(StringArraySchema, row.tags);

  return {
    id: row.id,
    sequentialId: row.sequential_id,
    method: row.method,
    url: row.url,
    host: row.host,
    headers,
    rawHeaders,
    body: '',
    timestamp: new Date(row.timestamp),
    source: row.source || 'proxy',
    threadId: row.thread_id ?? undefined,
    httpVersion: row.http_version || '1.1',
    status: row.status_code ?? undefined,
    responseStatusMessage: row.status_message ?? undefined,
    responseHeaders,
    responseRawHeaders,
    responseBody: undefined,
    responseHttpVersion: row.response_http_version ?? undefined,
    length: row.response_body_size || undefined,
    bodySize: row.body_size || undefined,
    responseBodySize: row.response_body_size || undefined,
    bodyRef: row.body_ref ?? undefined,
    bodyStorageType: (row.body_storage_type as 'inline' | 'file') ?? undefined,
    bodyContentType:
      (row.body_content_type as 'text' | 'binary' | 'multipart') ?? undefined,
    bodyPreview: row.body_preview ?? undefined,
    responseBodyRef: row.response_body_ref ?? undefined,
    responseBodyStorageType:
      (row.response_body_storage_type as 'inline' | 'file') ?? undefined,
    responseBodyContentType:
      (row.response_body_content_type as 'text' | 'binary') ?? undefined,
    responseBodyPreview: row.response_body_preview ?? undefined,
    wasIntercepted: row.was_intercepted === 1,
    wasProxyModified: row.was_proxy_modified === 1,
    originalRequest: parseStoredJsonField(
      ProxyModifiedSnapshotSchema,
      row.original_request,
    ),
    originalResponse: parseStoredJsonField(
      ProxyModifiedSnapshotSchema,
      row.original_response,
    ),
    highlight: (row.highlight as HighlightColor) ?? undefined,
    comment: row.comment ?? undefined,
    tags,
  };
}

type BuildSqliteFiltersArgs = {
  filterMode: FilterMode;
  searchTerm: string;
  methodFilter: MethodFilter;
  statusFilter: StatusFilter;
  mimeTypeFilter: MimeTypeFilter;
  extensionFilterMode: ExtensionFilterMode;
  selectedExtensions: string[];
  tqlSql: RequestSqlTranslation | null;
  sourceFilter?: SourceFilter;
  applicationScope?: ApplicationScopeFilter;
  excludeRules?: Array<{
    pattern: string;
    isRegex: boolean;
  }>;
};

function buildSqliteFilters({
  filterMode,
  searchTerm,
  methodFilter,
  statusFilter,
  mimeTypeFilter,
  extensionFilterMode,
  selectedExtensions,
  tqlSql,
  sourceFilter,
  applicationScope,
  excludeRules,
}: BuildSqliteFiltersArgs): HistoryFilters {
  const filters: HistoryFilters = {};

  if (filterMode === 'simple' && searchTerm) {
    filters.search = searchTerm;
  }

  if (filterMode === 'simple' && methodFilter.length > 0) {
    filters.methods = [...methodFilter];
  }

  if (filterMode === 'simple' && statusFilter.length > 0) {
    filters.statusBuckets = [...statusFilter];
  }

  if (filterMode === 'simple' && mimeTypeFilter.length > 0) {
    filters.mimeTypes = [...mimeTypeFilter];
  }

  if (
    filterMode === 'simple' &&
    selectedExtensions.length > 0 &&
    extensionFilterMode
  ) {
    filters.extensionFilterMode = extensionFilterMode as 'hide' | 'show_only';
    filters.selectedExtensions = selectedExtensions;
  }

  if (filterMode === 'advanced' && tqlSql) {
    filters.tqlWhereClause = tqlSql.whereClause;
    filters.tqlParams = tqlSql.params;
    filters.tqlRequiresBodyJoin = tqlSql.requiresBodyJoin;
  }

  if (sourceFilter && sourceFilter.length > 0) {
    filters.sources = sourceFilter;
  }

  if (applicationScope) {
    filters.applicationScope = applicationScope;
  }

  if (excludeRules && excludeRules.length > 0) {
    filters.excludeRules = excludeRules;
  }

  return filters;
}

type ShouldRefreshCacheForRowUpdatesArgs = {
  filterMode: FilterMode;
  searchTerm: string;
  statusFilter: StatusFilter;
  mimeTypeFilter: MimeTypeFilter;
  effectiveTqlQuery: string;
};

function shouldRefreshCacheForRowUpdates({
  filterMode,
  searchTerm,
  statusFilter,
  mimeTypeFilter,
  effectiveTqlQuery,
}: ShouldRefreshCacheForRowUpdatesArgs): boolean {
  if (filterMode === 'advanced') {
    return effectiveTqlQuery.trim().length > 0;
  }

  return (
    searchTerm.trim().length > 0 ||
    statusFilter.length > 0 ||
    mimeTypeFilter.length > 0
  );
}

function resolveHistoryTarget({
  applicationIdProp,
  projectTargetId,
  projectTargetType,
  storeTargetId,
  storeTargetType,
}: {
  applicationIdProp?: string | null;
  projectTargetId: string;
  projectTargetType: TargetTypeType;
  storeTargetId: string | null;
  storeTargetType: TargetTypeType | null;
}): { targetId: string; targetType: TargetTypeType } {
  const targetId = applicationIdProp || storeTargetId || 'standalone';

  if (targetId === projectTargetId) {
    return { targetId, targetType: projectTargetType };
  }

  if (targetId === storeTargetId && storeTargetType) {
    return { targetId, targetType: storeTargetType };
  }

  return {
    targetId,
    targetType:
      projectTargetType || storeTargetType || TARGET_TYPE.WEB_APPLICATION,
  };
}

function isNewestFirstHistorySort({
  sortField,
  sortOrder,
}: {
  sortField: SortField;
  sortOrder: SortOrder;
}): boolean {
  return sortField === 'timestamp' && sortOrder === 'desc';
}

function toPatchableHistoryRow(row: HttpRequest) {
  return {
    sequentialId: row.sequentialId,
    method: row.method,
    url: row.url,
    host: row.host,
    headers: row.headers,
    rawHeaders: row.rawHeaders,
    timestamp: row.timestamp.getTime(),
    source: row.source,
    httpVersion: row.httpVersion,
    wasIntercepted: row.wasIntercepted,
    wasEdited: row.wasEdited,
    status: row.status,
    responseStatusMessage: row.responseStatusMessage,
    responseHeaders: row.responseHeaders,
    responseRawHeaders: row.responseRawHeaders,
    responseHttpVersion: row.responseHttpVersion,
    length: row.length,
    bodySize: row.bodySize,
    responseBodySize: row.responseBodySize,
    bodyRef: row.bodyRef,
    bodyStorageType: row.bodyStorageType,
    bodyContentType: row.bodyContentType,
    bodyPreview: row.bodyPreview,
    responseBodyRef: row.responseBodyRef,
    responseBodyStorageType: row.responseBodyStorageType,
    responseBodyContentType: row.responseBodyContentType,
    responseBodyPreview: row.responseBodyPreview,
    highlight: row.highlight,
    comment: row.comment,
    tags: row.tags,
    proxyError: row.proxyError,
    proxyWarnings: row.proxyWarnings,
  };
}

function shouldPatchHistoryRow({
  currentRow,
  updatedRow,
}: {
  currentRow: HttpRequest;
  updatedRow: HttpRequest | undefined;
}): boolean {
  if (!updatedRow) return false;

  return !isEqual(
    toPatchableHistoryRow(currentRow),
    toPatchableHistoryRow(updatedRow),
  );
}

function isHistoryFilterPending({
  filterMode,
  searchTerm,
  debouncedSearchTerm,
  effectiveTqlQuery,
  debouncedEffectiveTqlQuery,
}: {
  filterMode: FilterMode;
  searchTerm: string;
  debouncedSearchTerm: string;
  effectiveTqlQuery: string;
  debouncedEffectiveTqlQuery: string;
}): boolean {
  if (filterMode === 'simple') {
    return searchTerm !== debouncedSearchTerm;
  }

  return effectiveTqlQuery !== debouncedEffectiveTqlQuery;
}

function isHistoryGridLoading({
  isFetchingRows,
  isFilterPending,
  isAssigningDatasource,
}: {
  isFetchingRows: boolean;
  isFilterPending: boolean;
  isAssigningDatasource: boolean;
}): boolean {
  return isFetchingRows || isFilterPending || isAssigningDatasource;
}

export function createDatasource({
  targetId,
  targetType,
  filters,
  sortField,
  sortOrder,
  onFetchStart,
  onFetchEnd,
}: {
  targetId: string;
  targetType: string;
  filters: HistoryFilters;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  onFetchStart: () => void;
  onFetchEnd: () => void;
}): IDatasource {
  return {
    getRows: async (params: IGetRowsParams) => {
      onFetchStart();

      try {
        if (!window.electronAPI?.history?.getRows) {
          params.successCallback([], 0);

          return;
        }

        const sortModel =
          params.sortModel && params.sortModel.length > 0
            ? params.sortModel.map((s) => ({
                colId: s.colId,
                sort: s.sort as 'asc' | 'desc',
              }))
            : [{ colId: sortField || 'timestamp', sort: sortOrder || 'desc' }];

        const result = await window.electronAPI.history.getRows({
          targetId,
          targetType,
          startRow: params.startRow,
          endRow: params.endRow,
          sortModel,
          filters,
        });

        if (result.cancelled) {
          params.successCallback([], undefined);

          return;
        }

        if (!result.success || !result.data) {
          params.failCallback();

          return;
        }

        params.successCallback(
          result.data.rows.map(rowToHttpRequest),
          result.data.lastRow,
        );
      } catch (err) {
        console.error('[HistoryDatasource] Failed to fetch rows:', err);
        params.failCallback();
      } finally {
        onFetchEnd();
      }
    },
  };
}

type UseHistoryDatasourceResult = {
  datasource: IDatasource | null;
  cacheBlockSize: number;
  maxBlocksInCache: number;
  filteredCount: number | null;
  isLoadingGrid: boolean;
  isLoadingCount: boolean;
  refreshCache: () => void;
};

export function useHistoryDatasource({
  gridApi,
  applicationId: applicationIdProp,
  sourceFilter,
  applicationScope,
  containerRef,
}: {
  gridApi: GridApi<HttpRequest> | null;
  applicationId?: string | null;
  sourceFilter?: SourceFilter;
  applicationScope?: ApplicationScopeFilter;
  containerRef?: RefObject<HTMLElement | null>;
}): UseHistoryDatasourceResult {
  const projectTargetId = useProjectStore((state) => state.targetId);
  const projectTargetType = useProjectStore((state) => state.targetType);
  const storeTargetId = useProxyStore((state) => state.currentTargetId);
  const storeTargetType = useProxyStore((state) => state.currentTargetType);
  const { targetId: currentTargetId, targetType: currentTargetType } = useMemo(
    () =>
      resolveHistoryTarget({
        applicationIdProp,
        projectTargetId,
        projectTargetType,
        storeTargetId,
        storeTargetType,
      }),
    [
      applicationIdProp,
      projectTargetId,
      projectTargetType,
      storeTargetId,
      storeTargetType,
    ],
  );
  const historyVersion = useProxyStore((state) => state.historyVersion);
  const historyStructureVersion = useProxyStore(
    (state) => state.historyStructureVersion,
  );
  const historyRowUpdateVersion = useProxyStore(
    (state) => state.historyRowUpdateVersion,
  );

  const {
    searchTerm,
    methodFilter,
    statusFilter,
    sortField,
    sortOrder,
    mimeTypeFilter,
    extensionFilterMode,
    selectedExtensions,
    filterMode,
    tqlQuery,
  } = useTabScopedRequestsFilters();

  const activeQuery = useFilterPresetsStore((s) => s.activeQuery);
  const resolvePreset = useFilterPresetsStore((s) => s.resolvePreset);
  const exclusionRules = useExclusionRulesStore((state) => state.rules);
  const excludeRules = useMemo(
    () =>
      exclusionRules
        .filter((rule) => rule.enabled && rule.type === 'exclude')
        .map((rule) => ({
          pattern: rule.pattern,
          isRegex: rule.isRegex,
        })),
    [exclusionRules],
  );

  const effectiveTqlQuery = useMemo(
    () => (filterMode === 'advanced' ? activeQuery || tqlQuery : ''),
    [activeQuery, tqlQuery, filterMode],
  );

  const [debouncedSearchTerm] = useDebounceValue(
    filterMode === 'simple' ? searchTerm : '',
    HISTORY_FILTER_DEBOUNCE_MS,
  );

  const [debouncedEffectiveTqlQuery] = useDebounceValue(
    effectiveTqlQuery,
    HISTORY_FILTER_DEBOUNCE_MS,
  );

  const tqlSql = useMemo(
    () => translateTqlToRequestSql(debouncedEffectiveTqlQuery, resolvePreset),
    [debouncedEffectiveTqlQuery, resolvePreset],
  );
  const refreshCacheForRowUpdates = useMemo(
    () =>
      shouldRefreshCacheForRowUpdates({
        filterMode,
        searchTerm,
        statusFilter,
        mimeTypeFilter,
        effectiveTqlQuery: debouncedEffectiveTqlQuery,
      }),
    [
      filterMode,
      searchTerm,
      statusFilter,
      mimeTypeFilter,
      debouncedEffectiveTqlQuery,
    ],
  );
  const isNewestFirstSort = useMemo(
    () =>
      isNewestFirstHistorySort({
        sortField: sortField || 'timestamp',
        sortOrder: sortOrder || 'desc',
      }),
    [sortField, sortOrder],
  );

  const filters = useMemo(
    () =>
      buildSqliteFilters({
        filterMode,
        searchTerm: debouncedSearchTerm,
        methodFilter,
        statusFilter,
        mimeTypeFilter,
        extensionFilterMode,
        selectedExtensions,
        tqlSql,
        sourceFilter,
        applicationScope,
        excludeRules,
      }),
    [
      filterMode,
      debouncedSearchTerm,
      methodFilter,
      statusFilter,
      mimeTypeFilter,
      extensionFilterMode,
      selectedExtensions,
      tqlSql,
      sourceFilter,
      applicationScope,
      excludeRules,
    ],
  );

  const isFilterPending = useMemo(
    () =>
      isHistoryFilterPending({
        filterMode,
        searchTerm,
        debouncedSearchTerm,
        effectiveTqlQuery,
        debouncedEffectiveTqlQuery,
      }),
    [
      filterMode,
      searchTerm,
      debouncedSearchTerm,
      effectiveTqlQuery,
      debouncedEffectiveTqlQuery,
    ],
  );

  const [isFetchingRows, setIsFetchingRows] = useState(false);
  const [isAssigningDatasource, setIsAssigningDatasource] = useState(false);
  const pendingFetchCountRef = useRef(0);

  const markRowsFetchStarted = useCallback(() => {
    pendingFetchCountRef.current += 1;
    setIsFetchingRows(true);
  }, []);

  const markRowsFetchFinished = useCallback(() => {
    pendingFetchCountRef.current = Math.max(
      0,
      pendingFetchCountRef.current - 1,
    );
    if (pendingFetchCountRef.current === 0) {
      setIsFetchingRows(false);
    }
  }, []);

  const datasource = useMemo(() => {
    if (!currentTargetId) return null;

    return createDatasource({
      targetId: currentTargetId,
      targetType: currentTargetType,
      filters,
      sortField: sortField || 'timestamp',
      sortOrder: sortOrder || 'desc',
      onFetchStart: markRowsFetchStarted,
      onFetchEnd: markRowsFetchFinished,
    });
  }, [
    currentTargetId,
    currentTargetType,
    filters,
    sortField,
    sortOrder,
    markRowsFetchStarted,
    markRowsFetchFinished,
  ]);

  const isLoadingGrid = isHistoryGridLoading({
    isFetchingRows,
    isFilterPending,
    isAssigningDatasource,
  });

  const prevDatasourceRef = useRef<IDatasource | null>(null);
  const datasourceAssignGenerationRef = useRef(0);
  const querySyncRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (
      !datasource ||
      !gridApi ||
      gridApi.isDestroyed() ||
      datasource === prevDatasourceRef.current
    ) {
      return;
    }

    const generation = ++datasourceAssignGenerationRef.current;
    const datasourceToAssign = datasource;

    setIsAssigningDatasource(true);

    async function assignDatasource() {
      try {
        await window.electronAPI?.history?.cancelStaleReads?.();
        if (generation !== datasourceAssignGenerationRef.current) return;
        if (!gridApi || gridApi.isDestroyed()) return;

        prevDatasourceRef.current = datasourceToAssign;
        gridApi.setGridOption('datasource', datasourceToAssign);
      } finally {
        if (generation === datasourceAssignGenerationRef.current) {
          setIsAssigningDatasource(false);
        }
      }
    }

    const syncPromise = assignDatasource();

    querySyncRef.current = syncPromise;
    void syncPromise;
  }, [datasource, gridApi]);

  const prevStructureVersionRef = useRef(historyStructureVersion);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const removeModelUpdatedListenerRef = useRef<(() => void) | null>(null);
  const pendingRefreshRef = useRef(false);
  const viewportRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    viewportRef.current = getAgGridViewport(containerRef);
  }, [gridApi, containerRef]);

  const clearScheduledRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (anchorRestoreTimeoutRef.current) {
      clearTimeout(anchorRestoreTimeoutRef.current);
      anchorRestoreTimeoutRef.current = null;
    }

    removeModelUpdatedListenerRef.current?.();
    removeModelUpdatedListenerRef.current = null;
  }, []);

  const scheduleCacheRefresh = useCallback(() => {
    if (!gridApi || gridApi.isDestroyed()) return;

    clearScheduledRefresh();
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      if (!gridApi || gridApi.isDestroyed()) return;

      const viewport = viewportRef.current;

      if (!viewport) return;

      const edge = detectAgGridScrollEdge(viewport);

      if (edge === 'middle') {
        pendingRefreshRef.current = true;
      } else if (edge === 'top' && isNewestFirstSort) {
        pendingRefreshRef.current = false;
        gridApi.refreshInfiniteCache();
      } else {
        pendingRefreshRef.current = false;
        const anchor = captureAgGridRowScrollAnchor({
          gridApi,
          containerRef,
        });
        const restore = () => {
          if (!anchor || gridApi.isDestroyed()) return;
          restoreAgGridRowScrollAnchor({ gridApi, containerRef, anchor });
        };

        const restoreAfterModelUpdate = () => {
          removeModelUpdatedListenerRef.current?.();
          removeModelUpdatedListenerRef.current = null;
          requestAnimationFrame(restore);
        };

        if (anchor) {
          gridApi.addEventListener('modelUpdated', restoreAfterModelUpdate);
          removeModelUpdatedListenerRef.current = () => {
            gridApi.removeEventListener(
              'modelUpdated',
              restoreAfterModelUpdate,
            );
          };

          anchorRestoreTimeoutRef.current = setTimeout(() => {
            anchorRestoreTimeoutRef.current = null;
            if (gridApi.isDestroyed()) return;
            removeModelUpdatedListenerRef.current?.();
            removeModelUpdatedListenerRef.current = null;
            restore();
          }, 500);
        }

        gridApi.refreshInfiniteCache();
      }
    }, 200);
  }, [clearScheduledRefresh, gridApi, containerRef, isNewestFirstSort]);

  useEffect(() => clearScheduledRefresh, [clearScheduledRefresh]);

  useEffect(() => {
    if (historyStructureVersion === prevStructureVersionRef.current) return;
    prevStructureVersionRef.current = historyStructureVersion;

    scheduleCacheRefresh();

    return clearScheduledRefresh;
  }, [historyStructureVersion, scheduleCacheRefresh, clearScheduledRefresh]);

  const prevRowUpdateVersionRef = useRef(historyRowUpdateVersion);

  useEffect(() => {
    if (historyRowUpdateVersion === prevRowUpdateVersionRef.current) return;
    prevRowUpdateVersionRef.current = historyRowUpdateVersion;

    if (!gridApi || gridApi.isDestroyed()) return;

    if (refreshCacheForRowUpdates) {
      scheduleCacheRefresh();

      return clearScheduledRefresh;
    }

    const requests = useProxyStore.getState().requests;

    gridApi.forEachNode((rowNode) => {
      const currentRow = rowNode.data;

      if (!currentRow) return;

      const updatedRow = requests[currentRow.id];

      if (!shouldPatchHistoryRow({ currentRow, updatedRow })) return;

      rowNode.setData({
        ...currentRow,
        ...updatedRow,
      });
    });
  }, [
    historyRowUpdateVersion,
    clearScheduledRefresh,
    gridApi,
    refreshCacheForRowUpdates,
    scheduleCacheRefresh,
  ]);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;

    const viewport = viewportRef.current;

    if (!viewport) return;

    const onScroll = () => {
      if (!pendingRefreshRef.current || !gridApi || gridApi.isDestroyed())
        return;

      if (detectAgGridScrollEdge(viewport) !== 'middle') {
        pendingRefreshRef.current = false;
        scheduleCacheRefresh();
      }
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [gridApi, containerRef, scheduleCacheRefresh]);

  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const countAbortRef = useRef(0);
  const hasLoadedCountRef = useRef(false);

  // Stable ref required: usehooks-ts v3.1.1 includes `options` in useMemo deps (GH #703)
  const debounceOptions = useMemo(
    () => ({ leading: true, trailing: true, maxWait: 1000 }),
    [],
  );
  const [debouncedHistoryVersion] = useDebounceValue(
    historyVersion,
    1000,
    debounceOptions,
  );

  useEffect(() => {
    if (!currentTargetId) {
      setFilteredCount(null);
      setIsLoadingCount(false);
      hasLoadedCountRef.current = false;

      return;
    }

    if (!hasLoadedCountRef.current) {
      setIsLoadingCount(true);
    }

    const generation = ++countAbortRef.current;

    async function loadFilteredCount() {
      await querySyncRef.current;

      const result = await window.electronAPI?.history?.count({
        targetId: currentTargetId,
        targetType: currentTargetType,
        filters,
      });

      if (generation !== countAbortRef.current) return;
      if (result?.cancelled) return;
      if (result?.success && result.data) {
        hasLoadedCountRef.current = true;
        setFilteredCount(result.data.count);
      }
    }

    void loadFilteredCount()
      .catch((error: unknown) => {
        if (generation !== countAbortRef.current) return;
        console.warn('[HistoryDatasource] Failed to fetch filtered count', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      })
      .finally(() => {
        if (generation === countAbortRef.current) {
          setIsLoadingCount(false);
        }
      });
  }, [currentTargetId, currentTargetType, filters, debouncedHistoryVersion]);

  const refreshCache = useCallback(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.refreshInfiniteCache();
    }
  }, [gridApi]);

  return {
    datasource,
    cacheBlockSize: CACHE_BLOCK_SIZE,
    maxBlocksInCache: MAX_BLOCKS_IN_CACHE,
    filteredCount,
    isLoadingGrid,
    isLoadingCount,
    refreshCache,
  };
}

export {
  rowToHttpRequest,
  parseStatusFilter,
  buildSqliteFilters,
  CACHE_BLOCK_SIZE,
  shouldRefreshCacheForRowUpdates,
  shouldPatchHistoryRow,
  isNewestFirstHistorySort,
  resolveHistoryTarget,
  isHistoryFilterPending,
  isHistoryGridLoading,
};
